import React, { useState, useEffect } from 'react';
import { CommentForm } from './CommentForm';
import { CommentDisplay } from './CommentDisplay';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MessageCircle, Plus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface CommentSectionProps {
  sequenceId: string;
  sequenceName?: string;
  className?: string;
}

interface CommentStats {
  total_comments: number;
}

export const CommentSection: React.FC<CommentSectionProps> = ({
  sequenceId,
  sequenceName,
  className = ''
}) => {
  const { user, isLoading } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [stats, setStats] = useState<CommentStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  const handleCommentSuccess = () => {
    // Refresh the comment display
    setRefreshKey(prev => prev + 1);
    // Hide the comment form
    setShowCommentForm(false);
    // Reload stats
    loadStats();
  };

  const loadStats = async () => {
    try {
      setLoadingStats(true);
      const { count, error } = await supabase
        .from('sequence_comments')
        .select('*', { count: 'exact', head: true })
        .eq('sequence_id', sequenceId);

      if (error) throw error;

      setStats({ total_comments: count || 0 });
    } catch (error) {
      console.error('Error loading comment stats:', error);
      setStats({ total_comments: 0 });
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, [sequenceId, refreshKey]);

  const WriteCommentSection = () => {
    if (isLoading) {
      return (
        <Card className="mb-6">
          <CardContent className="pt-6 text-center">
            <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300 animate-pulse" />
            <h3 className="font-medium mb-2">Loading...</h3>
            <p className="text-gray-600 mb-4">
              Checking authentication status
            </p>
          </CardContent>
        </Card>
      );
    }

    if (!user) {
      return (
        <Card className="mb-6">
          <CardContent className="pt-6 text-center">
            <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="font-medium mb-2">Join the Conversation</h3>
            <p className="text-gray-600 mb-4">
              Sign in to share your thoughts and connect with the community
            </p>
            <Button variant="outline">
              Sign In to Comment
            </Button>
          </CardContent>
        </Card>
      );
    }

    if (showCommentForm) {
      return (
        <Card className="mb-6">
          <CardContent className="pt-6">
            <CommentForm
              sequenceId={sequenceId}
              sequenceName={sequenceName}
              onSuccess={handleCommentSuccess}
              onCancel={() => setShowCommentForm(false)}
            />
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium mb-1">Share your thoughts</h3>
              <p className="text-sm text-gray-600">
                Join the discussion about this sequence
              </p>
            </div>
            <Button onClick={() => setShowCommentForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Comment
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const hasComments = stats && stats.total_comments > 0;

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-bold">Comments</h2>
          {!loadingStats && stats && (
            <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {stats.total_comments} {stats.total_comments === 1 ? 'comment' : 'comments'}
            </span>
          )}
        </div>
      </div>

      {/* Write Comment Section */}
      <WriteCommentSection />

      {/* Comments Display - Only shown when comments exist */}
      {!loadingStats && hasComments && (
        <CommentDisplay
          key={refreshKey}
          sequenceId={sequenceId}
        />
      )}

      {/* Loading state for stats */}
      {loadingStats && (
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-lg"></div>
        </div>
      )}

      {/* No comments state */}
      {!loadingStats && !hasComments && user && (
        <Card>
          <CardContent className="pt-6 text-center">
            <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="font-medium mb-2">No comments yet</h3>
            <p className="text-gray-600 mb-4">
              Be the first to share your thoughts about this sequence!
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CommentSection;